import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import {
  KAPCSOLAT_CELOK,
  KAPCSOLAT_CEL_NEVEK,
  api,
  type EtteremLista,
  type EtteremSor,
  type KapcsolatCel,
  type Szuro,
} from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { ft, lejaratOsztaly } from '../formazas.ts';
import { letolt } from '../letoltes.ts';
import { Mezo, SzamMezo, ValasztoMezo } from './Mezo.tsx';
import { CsvVisszatoltes } from './CsvVisszatoltes.tsx';
import { Mentesek } from './Mentesek.tsx';
import { EtteremReszletek } from './EtteremReszletek.tsx';
import { UjEtterem } from './UjEtterem.tsx';

/** Adószám alakja: 12345678-2-42 */
const ADOSZAM_MINTA = /^\d{8}-\d-\d{2}$/;

const SZURO_SORREND: Szuro[] = [
  'mind',
  'olaj',
  'gyujtes',
  'nincsar',
  'duplikalt',
  'tobbtelephely',
  'rosszadoszam',
  'hianyos',
  'nincsemail',
  'inaktiv',
];

export function Ettermek({
  fejlec,
  onKarton,
  kezdoSzuro,
  kezdoKereses,
}: {
  kezdoSzuro?: Szuro;
  kezdoKereses?: string;
  /** A képernyő fejlécébe kerülő alfül-váltó — a szülő adja. */
  fejlec?: React.ReactNode;
  onKarton: (etterem_id: number) => void;
}) {
  /** Új partnert csak külön engedéllyel lehet felvenni. (3.0 / 1.) */
  const felvehet = useMuvelet('partner_uj');
  /** A fájlból betöltés és a mentés is külön engedély. (3.0 / 1.) */
  const betolthet = useMuvelet('import');
  const menthet = useMuvelet('mentes');
  const alapadatok = useAlapadatok();

  /** Letöltés csak engedéllyel. (3.0 / 1.) */
  const letolthet = useMuvelet('export_torzs');
  const [kereses, setKereses] = useState(kezdoKereses ?? '');
  const [szuro, setSzuro] = useState<Szuro>(kezdoSzuro ?? 'mind');
  const [lista, setLista] = useState<EtteremLista | null>(null);
  const [toltes, setToltes] = useState(true);
  const [hiba, setHiba] = useState<string | null>(null);
  const [nyitottId, setNyitottId] = useState<number | null>(null);
  const [ujNyitva, setUjNyitva] = useState(false);
  const [csvNyitva, setCsvNyitva] = useState(false);
  const [mentesNyitva, setMentesNyitva] = useState(false);

  // A moslék alapára a Termékekből — az üres „MO ár" mező helykitöltője.
  const [moAlapAr, setMoAlapAr] = useState<number | null>(null);
  useEffect(() => {
    api
      .get<{ sorok: Array<{ kod: string; brutto_ertek: number | null }> }>('/api/termek')
      .then((v) => setMoAlapAr(v.sorok.find((t) => t.kod === 'MO')?.brutto_ertek ?? null))
      .catch(() => setMoAlapAr(null));
  }, []);

  // A keresésnél megvárjuk, hogy abbahagyd a gépelést — így nem indul
  // kérés minden leütésre.
  const [keresesKesleltetve, setKeresesKesleltetve] = useState(kezdoKereses ?? '');
  useEffect(() => {
    const idozito = setTimeout(() => setKeresesKesleltetve(kereses), 220);
    return () => clearTimeout(idozito);
  }, [kereses]);

  const betolt = useCallback(async () => {
    setToltes(true);
    try {
      const p = new URLSearchParams();
      if (keresesKesleltetve.trim()) p.set('q', keresesKesleltetve.trim());
      if (szuro !== 'mind') p.set('szuro', szuro);
      const eredmeny = await api.get<EtteremLista>(`/api/etterem?${p}`);
      setLista(eredmeny);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a listát.');
    } finally {
      setToltes(false);
    }
  }, [keresesKesleltetve, szuro]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /**
   * Egy mező mentése. A szerver a teljes, frissített éttermet küldi vissza —
   * azt írjuk be a listába, hogy a számított mezők (bruttó ár) is stimmeljenek,
   * anélkül hogy az egész listát újratöltenénk.
   */
  const mentMezo = useCallback(
    async (id: number, mezo: string, ertek: unknown) => {
      const frissitett = await api.patch<EtteremSor>(`/api/etterem/${id}/mezo/${mezo}`, {
        ertek,
      });
      /*
       * Az aktív jelölő és a csak gyűjtés **a szűrők darabszámát is
       * átírja**: az inaktív partner nem duplikált többé, a csak
       * gyűjtésesnek pedig nem kell ár. Ilyenkor az egész listát újra
       * kérjük, különben a csipeken a régi szám maradna kint.
       * (A megrendelő jelzése, 2026-10-20.)
       */
      if (mezo === 'aktiv' || mezo === 'csak_gyujtes') {
        await betolt();
        return;
      }
      setLista((elozo) =>
        elozo
          ? {
              ...elozo,
              sorok: elozo.sorok.map((s) => (s.id === id ? { ...s, ...frissitett } : s)),
            }
          : elozo,
      );
    },
    [betolt],
  );

  /*
   * Az e-mail cím **nem** az étterem tábla mezője, hanem külön elérhetőség —
   * ezért saját végponton megy, nem a /mezo/ útvonalon. A válasz a partner
   * frissített sora, ugyanúgy beolvad a listába. (2026-10-07)
   */
  const mentEmail = useCallback(async (id: number, ertek: string, cel?: KapcsolatCel) => {
    const frissitett = await api.patch<EtteremSor>(`/api/etterem/${id}/email`, {
      ertek: ertek.trim() || null,
      ...(cel ? { cel } : {}),
    });
    setLista((elozo) =>
      elozo
        ? {
            ...elozo,
            sorok: elozo.sorok.map((s) => (s.id === id ? { ...s, ...frissitett } : s)),
          }
        : elozo,
    );
  }, []);

  const fizetesiLehetosegek = useMemo(
    () =>
      alapadatok.fizetesi_modok
        .filter((m) => m.kod !== 'KP')
        .map((m) => ({ ertek: m.kod, cimke: m.nev })),
    [alapadatok.fizetesi_modok],
  );

  const sorok = lista?.sorok ?? [];

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Partnerek</span>
        {fejlec}
        <input
          className="qi"
          style={{ width: 300, fontSize: 12.5 }}
          placeholder="keresés kód, név, cím, cégnév, adószám…"
          value={kereses}
          onChange={(e) => setKereses(e.target.value)}
        />
        <div className="stats">
          <div>
            Étterem
            <strong>{ft(lista?.osszes ?? 0)}</strong>
          </div>
          <div>
            Szűrve
            <strong>{ft(lista?.talalat ?? 0)}</strong>
          </div>
        </div>
      </div>

      <div className="fchips">
        {SZURO_SORREND.map((k) => (
          <button
            key={k}
            className={`fchip${szuro === k ? ' on' : ''}`}
            onClick={() => setSzuro(k)}
          >
            {alapadatok.etterem_szurok[k]}
            <b>{ft(lista?.szuro_darabok[k] ?? 0)}</b>
          </button>
        ))}
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}

      {nyitottId !== null && (
        <EtteremReszletek
          id={nyitottId}
          onBezar={() => setNyitottId(null)}
          onValtozott={() => void betolt()}
          onKarton={onKarton}
        />
      )}

      <div className="actbar" style={{ marginTop: 0 }}>
        {felvehet && (
          <button className="btn sm" onClick={() => setUjNyitva((v) => !v)}>
            {ujNyitva ? '× Mégsem' : '+ Új étterem felvétele'}
          </button>
        )}
        {letolthet && (
          <button className="btn sm" onClick={() => letolt('/api/export/torzs.xlsx')}>
            Excel
          </button>
        )}
        {letolthet && (
          <button className="btn sm" onClick={() => letolt('/api/export/torzs.csv')}>
            CSV letöltése szerkesztéshez
          </button>
        )}
        {betolthet && (
          <button className="btn sm" onClick={() => setCsvNyitva((v) => !v)}>
            {csvNyitva ? '× Mégsem' : 'CSV visszatöltése'}
          </button>
        )}
        <span style={{ width: 14 }}></span>
        {menthet && (
          <button className="btn sm" onClick={() => setMentesNyitva((v) => !v)}>
            {mentesNyitva ? '× Bezár' : 'Biztonsági mentés'}
          </button>
        )}
        <span className="hint">
          A táblázatban közvetlenül szerkeszthetsz. A mentés akkor történik, amikor
          elhagyod a mezőt.
        </span>
      </div>

      {csvNyitva && (
        <CsvVisszatoltes
          onKesz={() => {
            void betolt();
          }}
        />
      )}

      {mentesNyitva && <Mentesek />}

      {ujNyitva && (
        <UjEtterem
          onKesz={(id) => {
            setUjNyitva(false);
            void betolt();
            setNyitottId(id);
          }}
          onMegse={() => setUjNyitva(false)}
        />
      )}

      <div className="matrix" style={{ maxHeight: 620, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ width: 46 }} title="Partnerkód — a szerződésszám alapja">
                Kód
              </th>
              <th>Étterem</th>
              <th>Cím</th>
              <th className="n">Nettó</th>
              <th className="n">Bruttó</th>
              <th className="n">F+ nettó</th>
              <th
                className="n"
                title="A partner egyedi moslékára (bruttó, hordónként). Ha üres, a Termékek alapára számít."
              >
                MO ár
              </th>
              <th>Fizetés</th>
              <th
                title="A telephely saját e-mail címe. Ide megy a szállítólevél és a számla. Ha üres, a cég címére megy."
              >
                E-mail
              </th>
              <th>Megjegyzés</th>
              <th>Cégnév</th>
              <th className="n">MO lejárat</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((e) => (
              <EtteremSorNezet
                key={e.id}
                e={e}
                moAlapAr={moAlapAr}
                fizetesiLehetosegek={fizetesiLehetosegek}
                onMent={mentMezo}
                onMentEmail={mentEmail}
                onNyit={() => setNyitottId(e.id)}
              />
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={13} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {toltes ? 'Betöltés…' : 'Nincs találat.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function EtteremSorNezet({
  e,
  moAlapAr,
  fizetesiLehetosegek,
  onMent,
  onMentEmail,
  onNyit,
}: {
  e: EtteremSor;
  /** A moslék alapára a Termékekből — az üres MO ár mező helykitöltője. */
  moAlapAr: number | null;
  fizetesiLehetosegek: Array<{ ertek: string; cimke: string }>;
  onMent: (id: number, mezo: string, ertek: unknown) => Promise<void>;
  onMentEmail: (id: number, ertek: string, cel?: KapcsolatCel) => Promise<void>;
  onNyit: () => void;
}) {
  /*
   * A partner adatlapja három külön dolog: az **ár**, az **elérhetőség** és
   * minden más. Aki csak elérhetőséget rögzíthet (például a sofőr), annak a
   * többi mező nem szerkeszthető. (3.0 / 1.)
   */
  const arJog = useMuvelet('partner_ar');
  const modJog = useMuvelet('partner_mod');
  /** Az elérhetőség külön engedély — a sofőr is rögzíthet címet. (3.0 / 1.) */
  const elerhetosegJog = useMuvelet('partner_elerhetoseg');
  const vanAr = (e.netto_ar ?? 0) > 0 || (e.brutto_ar ?? 0) > 0;
  const ment = (mezo: string) => (ertek: string) => onMent(e.id, mezo, ertek);

  // Az első sorban a név, alatta a legbeszédesebb kiegészítő: több telephelyes
  // cégnél a cégnév és a telephelyszám, egyébként a cégcsoport vagy a jelölés.
  const alszoveg = (() => {
    if (e.telephelyek > 1) return `${e.cegnev ?? '—'} · ${e.telephelyek} telephely`;
    if (e.cegcsoport) return e.cegcsoport;
    if (e.csak_gyujtes) return null;
    return null;
  })();

  return (
    /*
     * Az inaktív partner a listán is látszik, de halványan és jelölve —
     * eddig csak a részletes lapon derült ki. (2026-10-14, a megrendelő
     * kérése)
     */
    <tr className={!e.aktiv ? 'nul' : vanAr ? undefined : 'gyr'}>
      <td className="calc2" style={{ textAlign: 'left', color: e.kod ? undefined : 'var(--ghost)' }}>
        {e.kod ?? '—'}
      </td>
      <td className="nm3" onClick={onNyit} title="Részletek megnyitása">
        <b>{e.nev}</b>
        {alszoveg && <small>{alszoveg}</small>}
        {!e.aktiv && (
          <small className="only" style={{ color: 'var(--bad)', fontWeight: 600 }}>
            inaktív
          </small>
        )}
        {e.csak_gyujtes && <small className="only">csak gyűjtés</small>}
        {!e.csak_gyujtes && !vanAr && !alszoveg && <small className="only">nincs ár</small>}
        {e.azonos_nevuek > 1 && (
          <small style={{ color: 'var(--flag)' }}>
            {e.azonos_nevuek}× szerepel ez a név
          </small>
        )}
      </td>
      <td>
        <Mezo
          ertek={[e.irsz, e.cim].filter(Boolean).join(' ')}
          onMent={ment('irsz_cim')}
          tiltva={!modJog}
          cim="Irányítószám és cím együtt, például: 1117 Fehérvári út 70"
        />
      </td>
      <td>
        <SzamMezo ertek={e.netto_ar} onMent={ment('netto_ar')} tiltva={!arJog} />
      </td>
      <td className="calc2">{e.brutto_ar ? ft(e.brutto_ar) : '—'}</td>
      <td>
        <SzamMezo ertek={e.fplusz_netto} onMent={ment('fplusz_netto')} tiltva={!arJog} />
      </td>
      <td title="A partner egyedi moslékára (bruttó, hordónként). Üresen a Termékek alapára számít.">
        <SzamMezo
          ertek={e.mo_ar}
          onMent={ment('mo_ar')}
          tiltva={!arJog}
          helykitolto={moAlapAr ? ft(moAlapAr) : '—'}
        />
      </td>
      <td>
        <ValasztoMezo
          ertek={e.fizetesi_mod}
          lehetosegek={fizetesiLehetosegek}
          uresCimke="Készpénz"
          onMent={ment('fizetesi_mod')}
          tiltva={!modJog}
        />
      </td>
      {/*
        * A telephely **saját** e-mail címe. Üresen a cég címe látszik
        * halványan: oda menne a levél. Egy cég öt telephelye mögött
        * ugyanaz a cégcím áll, a számlát és a szállítólevelet viszont
        * sokszor helyben kérik — ezért érdemes a sajátot megadni.
        * (2026-10-07, a megrendelő kérése)
        */}
      <td
        className="emcell"
        title={
          e.email
            ? undefined
            : e.ceg_email
              ? `Most a cég címére megy: ${e.ceg_email}`
              : 'Nincs cím — nem megy levél.'
        }
      >
        <div className="elerhetosor">
          <Mezo
            ertek={e.email}
            onMent={(ertek) => onMentEmail(e.id, ertek)}
            tiltva={!elerhetosegJog}
            helykitolto={e.ceg_email ? `${e.ceg_email} (cégé)` : '—'}
            cim="A telephely saját e-mail címe. Ide megy a szállítólevél és a számla."
          />
          {/*
            * Kategória a cím mellé: melyik levél megy rá. Enélkül csak a
            * részletes lapon volt állítható, és a felszólítás hiába kapott
            * számlázási címet. (2026-10-07, a megrendelő kérése)
            */}
          {e.email && (
            <select
              className="celvalaszto"
              value={e.email_cel ?? 'egyeb'}
              disabled={!elerhetosegJog}
              title="Mire jó ez a cím"
              onChange={(ev) =>
                void onMentEmail(e.id, e.email ?? '', ev.target.value as KapcsolatCel)
              }
            >
              {KAPCSOLAT_CELOK.map((c) => (
                <option key={c} value={c}>
                  {KAPCSOLAT_CEL_NEVEK[c]}
                </option>
              ))}
            </select>
          )}
          {e.email_db > 1 && (
            <button
              className="xb"
              title={`${e.email_db} cím — a részletes lapon mind látszik`}
              onClick={onNyit}
            >
              +{e.email_db - 1}
            </button>
          )}
        </div>
      </td>
      <td>
        <Mezo ertek={e.megjegyzes} onMent={ment('megjegyzes')} tiltva={!modJog} />
      </td>
      <td>
        <Mezo ertek={e.cegnev} onMent={ment('cegnev')} tiltva={!modJog} />
        {/*
          * Ha hiányos a számlázás, itt áll, hogy MI hiányzik. Enélkül a
          * „Hiányos számlázás" szűrő olyan partnereket is mutatott, akiknek
          * van adószámuk — és nem derült ki, hogy a székhely az, ami nincs.
          * (A megrendelő jelzése, 2026-10-21.)
          */}
        {e.hianyzo_szamlazas && (
          <span
            className="elojel hianyjel"
            title="A számlázáshoz cégnév, adószám és székhely kell. A cég adata is beszámít."
          >
            hiányzik: {e.hianyzo_szamlazas}
          </span>
        )}
      </td>
      <td className={`c ${lejaratOsztaly(e.mo_lejarat)}`} title="A legfrissebb MO szerződés lejárata">
        {e.mo_lejarat ?? '—'}
      </td>
      <td className="act" style={{ whiteSpace: 'nowrap' }}>
        {/*
          * Inaktiválás a listáról: eddig csak a részletes lapon volt.
          * Nem törlés — a partner adata és előzménye megmarad, csak a napi
          * munka keresői nem ajánlják fel. (2026-10-14, a megrendelő kérése)
          */}
        {modJog && (
          <button
            className={e.aktiv ? 'xb' : 'btn sm'}
            title={
              e.aktiv
                ? 'Inaktiválás: a napi munkában nem ajánlja fel. Az adatai megmaradnak.'
                : 'Visszakapcsolás'
            }
            onClick={() => void onMent(e.id, 'aktiv', !e.aktiv)}
          >
            {e.aktiv ? '⊘' : 'Vissza'}
          </button>
        )}{' '}
        <button className="xb" title="Részletek" onClick={onNyit}>
          ⋯
        </button>
      </td>
    </tr>
  );
}

export { ADOSZAM_MINTA };
